import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef, SyntheticEvent,
} from 'react';
import type { ComponentProps as PropsOf } from 'react';
import { loadModule, RenderModule } from 'holocron';
import type { HolocronLoadModuleDataParams as LMDP, LoadModuleData } from 'holocron';
import { FormattedMessage as I18n, IntlProvider } from 'react-intl';
import type { IntlConfig } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ProgressCircular } from '@americanexpress/dls-react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import PerformanceMeasure from '@americanexpress/browser-performance-mark';
import { useReducedMotion } from '@americanexpress/react-use-hooks';
import { FadeIn } from '@americanexpress/web-react-extensions';

import globalStyles from './global.scss';
import styles from './WarmWelcomeVideo.scss';
import type { ExploreModuleProps, Refresh } from './types';
import { Display } from './types';
import ShortSilentVideoModal from './video/ShortSilentVideoModal';
import {
  EXPLORE_MODULE_NAME,
  LOAD_MESSAGE,
  MODULE_NAME,
  PREFERENCE_KEY,
  WEB_HEADER,
} from '../constants';
import { reducer, SET_PREFER_NOT_SHOWN } from '../ducks';
import {
  getLocale,
  getPreferredNotShown,
  getProductBusinessType,
  getPublicGuid,
} from '../selectors';
import useImpression from '../hooks/FutureUHL/useImpression';
import ShortSilentVideo from './video/ShortSilentVideo';
import { flex } from '../utils';
import { SurfaceVariant } from './video/types';
import useOneDataFetchyeWithLogging from '../hooks/FutureUHL/logging/useOneDataFetchyeWithLogging';
import useOnce from '../hooks/FutureUHL/useOnce';
import useSplunkLogger from '../hooks/FutureUHL/logging/useSplunkLogger';

const { WELCOME_VIDEO } = Display;

export type Props = {
  refreshData: Refresh,
  url?: string,
  viewName?: string,
  // loadModuleData:
  languageData: IntlConfig['messages'],
};

export type MemberPreference = {
  type: string,
  value: boolean,
  accountToken: string,
};

export enum Stage {
  LOADING_VIDEO,
  VIDEO,
  LOADING_EXPLORE,
  EXPLORE,
}

export enum ExploreStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

type CreatePrefReq = {
  publicGuid: string,
  preferences: [{ type: typeof PREFERENCE_KEY, accountKeys: string[] }],
}

const WarmWelcomeVideo = ({
  languageData,
  refreshData: refresh,
  url: urlProp,
  viewName = 'Overview',
}: Props) => {
  /* The links data comes from our locale data structure,
   * but this is incompatible with the messages type from IntlConfig. */
  const links = languageData?.links as unknown as undefined | { videoUrl: string };
  const url = urlProp || links?.videoUrl;

  const log = useSplunkLogger();

  const locale = useSelector(getLocale);
  const publicGuid = useSelector(getPublicGuid);
  const isPreferredNotShown = useSelector(getPreferredNotShown);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [stage, setStage] = useState(Stage.LOADING_VIDEO);
  const [isVideoStage, setIsVideoStage] = useState(false);
  const [exploreStatus, setExploreStatus] = useState(ExploreStatus.LOADING);

  const { run: createNotShownPref } = useOneDataFetchyeWithLogging<CreatePrefReq>(
    'CreateMemberPreferences.v1',
    refresh,
    {
      defer: true,
      headers: WEB_HEADER,
      body: {
        publicGuid,
        preferences: [{
          type: PREFERENCE_KEY,
          accountKeys: [refresh.accountKey],
        }],
      },
    });
  const dispatch = useDispatch();
  const preferNotShown = useCallback(() => {
    dispatch({ type: SET_PREFER_NOT_SHOWN });
    createNotShownPref();
  }, [createNotShownPref, dispatch]);

  const sendImpression = useImpression();

  const skipAnalytics = useMemo(() => ({ element: 'Skip' }), []);

  const explore = useCallback(() => {
    setStage(exploreStatus === ExploreStatus.LOADING ? Stage.LOADING_EXPLORE : Stage.EXPLORE);
  }, [exploreStatus]);

  const onClose = useCallback(() => {
    if (stage < Stage.EXPLORE) {
      preferNotShown();
    }
  }, [preferNotShown, stage]);

  const containerProps = useMemo(() => ({
    variant: stage === Stage.EXPLORE ? 'base' : 'fg-dramatic',
  }), [stage]) satisfies { variant: SurfaceVariant | 'fg-dramatic' };

  type LoggingProps = {
    ok: boolean,
    status: string,
    error?: SyntheticEvent<HTMLVideoElement, Event> | Error,
  };

  const videoProps: Partial<PropsOf<typeof ShortSilentVideo>> = useMemo(() => {
    const logVideoPlayback = ({ ok, status, error }: LoggingProps) => {
      const errorMessage = videoRef.current?.error?.message;
      type ExtraVideoFields = { errorMessage?: string, errorCode?: number };
      log<ExtraVideoFields>(ok, {
        message: `Video ${status}`,
        error: ok ? null
          : error && Object.keys(error).length > 0 ? error : new Error(errorMessage),
        errorMessage,
        requestBody: { url },
        refresh,
      });
    };
    const onVideoStop = ({ ok, status, error }: LoggingProps) => {
      if (stage <= Stage.VIDEO) {
        explore();
        logVideoPlayback({ ok, status, error });
      }
    };

    return {
      onPlay: () => {
        setIsVideoStage(true);
        logVideoPlayback({ ok: true, status: 'started' });
      },
      onPlayError: (error) => {
        onVideoStop({ ok: false, status: `on-play-error-${error?.name}`, error });
      },
      onLoadedData: () => logVideoPlayback({ ok: true, status: 'loaded' }),
      onEnded: () => onVideoStop({ ok: true, status: 'finished' }),
      onError: (error) => onVideoStop({ ok: false, status: 'error', error }),
      onTimeout: () => onVideoStop({ ok: false, status: 'timeout' }),
      ref: videoRef,
    };
  }, [explore, log, refresh, stage, url]);

  const overlayContainerProps = useMemo(() => ({
    className: 'height-full width-full pointer-events-none',
  }), []);

  const exploreModuleProps: ExploreModuleProps = useMemo(() => ({
    refreshData: refresh,
    viewName: viewName.toLowerCase(),
    videoEnded: stage >= Stage.LOADING_EXPLORE,
    onLoad: () => setExploreStatus(ExploreStatus.SUCCESS),
    onError: () => setExploreStatus(ExploreStatus.ERROR),
  }), [refresh, stage, viewName]);

  useOnce(url, () => {
    log(true, {
      error: null,
      message: LOAD_MESSAGE,
      requestBody: { url },
      refresh,
    });
  });

  const reducedMotion = useReducedMotion();
  const productBusinessUnit = useSelector(getProductBusinessType(refresh.accountToken));
  const toPascalCase = (str: string) => str[0].toUpperCase() + str.slice(1).toLowerCase();
  const getImpressionTag = (status: string, type: string) =>
    `${toPascalCase(status)}${toPascalCase(type)}>>${toPascalCase(productBusinessUnit)}`;

  useEffect(() => {
    if (stage === Stage.LOADING_VIDEO && reducedMotion !== null) {
      if (!refresh?.display.includes(WELCOME_VIDEO) || reducedMotion) {
        explore();
      } else if (isVideoStage) {
        setStage(Stage.VIDEO);
        sendImpression({
          element: 'WelcomeVideo',
          detail: getImpressionTag(refresh.status, refresh.type),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run effect on loading change
  }, [isVideoStage, stage, reducedMotion]);

  useEffect(() => {
    if (stage === Stage.LOADING_EXPLORE && exploreStatus === ExploreStatus.SUCCESS) {
      setStage(Stage.EXPLORE);
    }
  }, [exploreStatus, stage]);

  useEffect(() => {
    if (stage === Stage.EXPLORE) {
      preferNotShown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run effect on stage change
  }, [stage]);

  if (isPreferredNotShown && stage < Stage.EXPLORE) {
    return null;
  }

  if (stage >= Stage.LOADING_EXPLORE && exploreStatus === ExploreStatus.ERROR) {
    return <div className="hidden" id={`${viewName.toLowerCase()}-explore-error`} />;
  }

  const videoPlayingTracerId = [
    MODULE_NAME,
    'playing',
    viewName.toLowerCase(),
    Boolean(stage === Stage.VIDEO),
  ].join('-');

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <PerformanceMeasure moduleName={MODULE_NAME} variable={refresh} />
      <div id={videoPlayingTracerId} className="hidden" />
      <ShortSilentVideoModal
        data-module-name={MODULE_NAME}
        surfaceVariant="fg-dramatic"
        className={globalStyles.shortSilentVideoModal}
        url={url}
        id={`${MODULE_NAME}-modal`}
        label="Modal with Video"
        showOverlay={stage === Stage.LOADING_VIDEO || stage === Stage.LOADING_EXPLORE}
        showContent={stage === Stage.EXPLORE}
        onClose={onClose}
        innerContainerClassName={styles.magicHeight}
        containerProps={containerProps as PropsOf<typeof ShortSilentVideoModal>['containerProps']}
        videoProps={videoProps}
        videoSized={false}
        headerContent={stage >= Stage.EXPLORE ? null : (
          <FadeIn isIn={stage === Stage.VIDEO} className="margin-auto-l margin-3-r">
            <Button
              onClick={explore}
              data-testid="skip"
              variant="secondary"
              reactlytics={skipAnalytics}
            >
              <span>
                <I18n id="skip" />
              </span>
            </Button>
          </FadeIn>
        )}
        overlayFadeProps={overlayContainerProps}
        overlay={(
          <div className={`height-full width-full ${flex('justify-center', 'align-items-center')}`}>
            {/* TODO: move Surface props to the container when Transitions become Surfaces */}
            <ProgressCircular />
          </div>
        )}
      >
        <RenderModule moduleName={EXPLORE_MODULE_NAME} props={exploreModuleProps} />
      </ShortSilentVideoModal>
    </IntlProvider>
  );
};

export const loadModuleData: LoadModuleData = ({ store: { dispatch } }: LMDP) => {
  dispatch(loadModule(EXPLORE_MODULE_NAME));
  return dispatch(loadLanguagePack(MODULE_NAME, { fallbackLocale: 'en-US' }));
};

WarmWelcomeVideo.holocron = {
  name: MODULE_NAME,
  loadModuleData,
  reducer,
};

export default oneAppModuleWrapper(MODULE_NAME)(WarmWelcomeVideo);
